import { CHANNELS } from "../shared/channels";
import { OWNER_PLACES, FEATURES } from "../shared/access";
import { MapsState } from "../shared/maps";
import { Track, MusicLibrary } from "../shared/music";
import { formatMoney, commonSymbol, jobOwed, jobPrice } from "../shared/money";
import { APP_SETTINGS } from "../shared/app_settings";
import { RouletteState, liveSlices, pickWeighted } from "../shared/roulette";
import { TierListSummary } from "../shared/tierlists";
import { BoardSummary } from "../shared/boards";
import {
  BotStatus, CalendarEvent, CharacterSheet, ClaudeUsage, Doodle, LogEntry, ModSize, PeopleBoard, ReferenceEntry, ServerOverview, StudioState
} from "../shared/types";
import { ShareBoard } from "../shared/sharing";
import { call, listen, mediaSrc } from "./bridge";
import { byId } from "./dom";
import { iconSvg, stampIcons } from "./icons";
import { locale, t } from "./i18n";
import { amOwner, canUse, myName } from "./me";
import { awardsNow, onAwards } from "./awards_store";
import { loadRefs, refsNow } from "./refs_store";
import { isRefFavorite, loadRefFavorites } from "./ref_favorites";
import { onPlayer, playQueue, playerState, next as playNext, previous as playPrevious, seek, toggle as playToggle, labelGradient, initials } from "./music_player";
import { onViewShown, showView } from "./navigation";
import { openReference } from "./views/references_view";
import { openOc } from "./views/ocs_view";
import { startGesture } from "./gesture_launch";
import { calculate, formatResult } from "./views/calculator_parser";
import { onTimer, timerReset, timerToggle, timerView } from "./views/studio_view";
import { FeedItem } from "../shared/achievements";
import { ago, feedLine } from "./views/feed_view";
import { loadFeed } from "./feed_store";

// home widgets: the places of chix as live modules, each one showing its own thing and not just a button to the place.
// the rack (home_rack.ts) adds each as a hidden module someone can switch on from the shelf, and calls refresh when it is shown

export interface WidgetDef {
  id: string;
  title: string;
  view: string;
  detail?: string;
  sizes: ModSize[];
  size: ModSize;
  // channels that mean the contents changed
  events?: string[];
  // fills the body once and returns what redraws it
  build: (body: HTMLElement) => () => void | Promise<void>;
}

function el<K extends keyof HTMLElementTagNameMap>(tag: K, className = "", text = ""): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text) node.textContent = text;
  return node;
}

function empty(text: string): HTMLElement {
  return el("p", "empty", t(text));
}

function line(main: string, meta = ""): HTMLElement {
  const row = el("li", "w-line");
  row.append(el("span", "w-line-main", main));
  if (meta) row.append(el("small", "w-line-meta", meta));
  return row;
}

function thumb(url: string, label = ""): HTMLElement {
  const frame = el("span", "w-thumb");
  const image = document.createElement("img");
  image.src = mediaSrc(url);
  image.alt = label;
  image.loading = "lazy";
  frame.append(image);
  return frame;
}

function key(icon: string, label: string, act: () => void, accent = false): HTMLButtonElement {
  const button = el("button", `pill icon-only${accent ? " accent" : ""}`);
  button.type = "button";
  button.setAttribute("aria-label", label);
  button.innerHTML = iconSvg(icon, 18);
  button.addEventListener("click", act);
  return button;
}

function pill(text: string, act: () => void, accent = false): HTMLButtonElement {
  const button = el("button", `pill small${accent ? " accent" : ""}`, text);
  button.type = "button";
  button.addEventListener("click", act);
  return button;
}

const today = (): string => {
  const now = new Date();
  const pad = (value: number) => String(value).padStart(2, "0");
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
};

// ---- the widgets

const music: WidgetDef = {
  id: "music", title: "music", view: "music", sizes: ["m", "l"], size: "m", events: [CHANNELS.musicUpdated],
  build(body) {
    const cover = el("span", "w-disc");
    const title = el("b", "w-music-title");
    const artist = el("small", "w-line-meta");
    const text = el("span", "w-music-text");
    text.append(title, artist);
    const play = key("play", t("play"), () => void start(), true);
    const keys = el("span", "w-keys");
    keys.append(key("back", t("previous song"), playPrevious), play, key("next", t("next song"), playNext));
    const top = el("div", "w-music");
    top.append(cover, text, keys);
    const bar = el("span", "w-bar");
    const fill = el("i");
    bar.append(fill);
    bar.addEventListener("click", (event) => {
      const state = playerState();
      const box = bar.getBoundingClientRect();
      if (state.duration) seek(((event.clientX - box.left) / box.width) * state.duration);
    });
    const hint = el("small", "w-line-meta");
    body.append(top, bar, hint);
    let tracks: Track[] = [];
    const start = async () => {
      if (playerState().track) return playToggle();
      if (!tracks.length) tracks = (await call<MusicLibrary>(CHANNELS.musicLibrary)).tracks;
      if (!tracks.length) return showView("music");
      playQueue(tracks, Math.floor(Math.random() * tracks.length));
    };
    const paint = () => {
      const state = playerState();
      const track = state.track;
      title.textContent = track ? track.title : t("nothing playing");
      artist.textContent = track ? track.artist : tracks.length ? t("{n} songs", { n: tracks.length }) : "";
      cover.style.background = track ? (track.coverUrl ? `center / cover url("${mediaSrc(track.coverUrl)}")` : labelGradient(track.title)) : "";
      cover.textContent = track && !track.coverUrl ? initials(track.title) : "";
      cover.classList.toggle("spin", state.playing);
      play.innerHTML = iconSvg(state.playing ? "pause" : "play", 18);
      fill.style.transform = `scaleX(${state.duration ? state.time / state.duration : 0})`;
      hint.textContent = track ? "" : t("tap play to shuffle your library");
    };
    onPlayer(() => {
      if (body.isConnected) paint();
    });
    return async () => {
      if (!tracks.length) tracks = (await call<MusicLibrary>(CHANNELS.musicLibrary).catch(() => ({ tracks: [] }))).tracks || [];
      paint();
    };
  }
};

const calendar: WidgetDef = {
  id: "when", title: "calendar", view: "calendar", sizes: ["s", "m", "l"], size: "m", events: [CHANNELS.calendarUpdated],
  build(body) {
    const list = el("ul", "w-list");
    body.append(list);
    return async () => {
      const events = await call<CalendarEvent[]>(CHANNELS.calendarList);
      const now = today();
      const coming = events.filter((item) => item.date >= now).sort((first, second) => `${first.date} ${first.time}`.localeCompare(`${second.date} ${second.time}`)).slice(0, 5);
      list.replaceChildren(...(coming.length ? coming.map((item) => {
        const day = item.date === now ? t("today") : new Date(`${item.date}T12:00`).toLocaleDateString(locale(), { weekday: "short", day: "numeric", month: "short" });
        return line(item.title, `${day}${item.time ? ` · ${item.time}` : ""}`);
      }) : [empty("nothing coming up")]));
    };
  }
};

const timer: WidgetDef = {
  id: "timer", title: "session timer", view: "studio", detail: "timer", sizes: ["s", "m"], size: "s",
  build(body) {
    const face = el("b", "w-clock");
    const meta = el("small", "w-line-meta");
    const go = key("play", t("start"), timerToggle, true);
    const keys = el("div", "w-keys");
    keys.append(go, key("refresh", t("reset"), timerReset));
    const bar = el("span", "w-bar");
    const fill = el("i");
    bar.append(fill);
    body.append(face, meta, bar, keys);
    const paint = () => {
      const view = timerView();
      face.textContent = view.clock;
      meta.textContent = `${t(view.mode)} · ${view.blocks === 1 ? t("1 block today") : t("{n} blocks today", { n: view.blocks })}`;
      go.innerHTML = iconSvg(view.running ? "pause" : "play", 18);
      fill.style.transform = `scaleX(${1 - view.share})`;
    };
    onTimer(() => {
      if (body.isConnected) paint();
    });
    return paint;
  }
};

const palette: WidgetDef = {
  id: "palette", title: "palette", view: "studio", detail: "palette", sizes: ["s", "m", "l"], size: "m", events: [CHANNELS.studioUpdated],
  build(body) {
    const grid = el("div", "w-swatches");
    body.append(grid);
    return async () => {
      const colors = (await call<StudioState>(CHANNELS.studioGet)).colors.slice(0, 18);
      grid.replaceChildren(...(colors.length ? colors.map((color) => {
        const swatch = el("button", "w-swatch");
        swatch.type = "button";
        swatch.style.background = color;
        swatch.setAttribute("aria-label", color);
        swatch.addEventListener("click", () => void navigator.clipboard?.writeText(color).then(() => (swatch.dataset.copied = "1"), () => undefined));
        return swatch;
      }) : [empty("no colours saved yet")]));
    };
  }
};

const prompts: WidgetDef = {
  id: "prompts", title: "prompt", view: "studio", detail: "prompts", sizes: ["m", "l"], size: "m", events: [CHANNELS.studioUpdated],
  build(body) {
    const title = el("b", "w-music-title");
    const text = el("p", "w-text");
    body.append(title, text, pill(t("another one"), () => void pick()));
    let cards: StudioState["prompts"] = [];
    const pick = async () => {
      if (!cards.length) cards = (await call<StudioState>(CHANNELS.studioGet)).prompts;
      const card = cards[Math.floor(Math.random() * cards.length)];
      title.textContent = card ? card.title : "";
      text.textContent = card ? card.text : t("no prompts saved yet");
    };
    return async () => {
      cards = [];
      await pick();
    };
  }
};

const jobs: WidgetDef = {
  id: "jobs", title: "commissions", view: "studio", detail: "jobs", sizes: ["m", "l"], size: "m", events: [CHANNELS.studioUpdated],
  build(body) {
    const owed = el("b", "w-clock");
    const list = el("ul", "w-list");
    body.append(owed, list);
    return async () => {
      const studio = await call<StudioState>(CHANNELS.studioGet);
      const open = studio.jobs.filter((job) => job.status !== APP_SETTINGS.jobStatuses[APP_SETTINGS.jobStatuses.length - 1]);
      const sign = commonSymbol(studio.jobs.map((job) => job.amount));
      const money = studio.jobs.reduce((sum, job) => sum + jobOwed(job), 0);
      owed.textContent = money > 0 ? t("{owed} owed to you", { owed: formatMoney(money, sign) }) : t("nobody owes you");
      list.replaceChildren(...(open.length ? open.slice(0, 4).map((job) => line(job.name, [t(job.status), jobPrice(job) ? formatMoney(jobPrice(job), sign) : "", job.due].filter(Boolean).join(" · "))) : [empty("no open commissions")]));
    };
  }
};

const calc: WidgetDef = {
  id: "calc", title: "calculator", view: "calculator", sizes: ["s", "m"], size: "s",
  build(body) {
    const form = el("form", "inline-form");
    const input = el("input");
    input.placeholder = "12 * 3.5 + 4";
    input.autocomplete = "off";
    input.setAttribute("aria-label", t("calculate"));
    input.inputMode = "text";
    form.append(input);
    const out = el("b", "w-clock");
    body.append(form, out);
    const run = () => {
      if (!input.value.trim()) {
        out.textContent = "";
        return;
      }
      try {
        out.textContent = `= ${formatResult(calculate(input.value))}`;
      } catch {
        out.textContent = "";
      }
    };
    input.addEventListener("input", run);
    form.addEventListener("submit", (event) => event.preventDefault());
    return () => undefined;
  }
};

const roulette: WidgetDef = {
  id: "roulette", title: "spin", view: "roulette", sizes: ["s", "m"], size: "s",
  build(body) {
    const select = el("select");
    select.setAttribute("aria-label", t("wheel"));
    const result = el("b", "w-result");
    const go = pill(t("spin"), () => void spin(), true);
    const row = el("div", "row");
    row.append(select, go);
    body.append(row, result);
    let state: RouletteState = { wheels: [] };
    const spin = async () => {
      const wheel = state.wheels.find((item) => item.id === select.value);
      const slices = wheel ? liveSlices(wheel) : [];
      const at = pickWeighted(slices, crypto.getRandomValues(new Uint32Array(1))[0] / 2 ** 32);
      if (!wheel || at < 0) return;
      result.textContent = `${slices[at].emoji} ${slices[at].label}`.trim();
      state = await call<RouletteState>(CHANNELS.rouletteSpun, wheel.id, slices[at].id).catch(() => state);
    };
    return async () => {
      state = await call<RouletteState>(CHANNELS.rouletteGet);
      const keep = select.value;
      select.replaceChildren(...state.wheels.map((wheel) => Object.assign(el("option"), { value: wheel.id, textContent: wheel.name })));
      if (keep) select.value = keep;
      go.disabled = !state.wheels.length;
      if (!state.wheels.length) result.textContent = t("make a wheel in spin first");
    };
  }
};

const maps: WidgetDef = {
  id: "maps", title: "places", view: "maps", sizes: ["s", "m", "l"], size: "m", events: [CHANNELS.mapsUpdated],
  build(body) {
    const list = el("ul", "w-list");
    body.append(list);
    return async () => {
      const saved = await call<MapsState>(CHANNELS.mapsGet);
      list.replaceChildren(...(saved.places.length ? saved.places.slice(0, 5).map((place) => line(place.name, place.address || "")) : [empty("no saved places yet")]));
    };
  }
};

const scan: WidgetDef = {
  id: "scan", title: "scans", view: "scan", sizes: ["s", "m", "l"], size: "m", events: [CHANNELS.scansUpdated],
  build(body) {
    const strip = el("div", "w-strip");
    body.append(strip);
    return async () => {
      const library = (await call<{ library: Array<{ id: string; name: string; fileUrl: string }> }>(CHANNELS.scansList)).library || [];
      strip.replaceChildren(...(library.length ? library.slice(0, 4).map((item) => thumb(item.fileUrl, item.name)) : [empty("no scans yet")]));
    };
  }
};

const doodles: WidgetDef = {
  id: "doodles", title: "doodle wall", view: "doodles", sizes: ["m", "l"], size: "m", events: [CHANNELS.doodlesUpdated],
  build(body) {
    const strip = el("div", "w-strip");
    body.append(strip);
    return async () => {
      const list = (await call<Doodle[]>(CHANNELS.doodlesList)).slice(0, 6);
      strip.replaceChildren(...(list.length ? list.map((item) => thumb(item.thumbUrl || item.url, item.title)) : [empty("the wall is empty")]));
    };
  }
};

const whiteboard: WidgetDef = {
  id: "whiteboard", title: "whiteboards", view: "whiteboard", sizes: ["s", "m", "l"], size: "m", events: [CHANNELS.boardsUpdated],
  build(body) {
    const strip = el("div", "w-strip");
    body.append(strip);
    return async () => {
      const boards = (await call<BoardSummary[]>(CHANNELS.boardsList)).slice(0, 4);
      strip.replaceChildren(...(boards.length ? boards.map((item) => {
        const tile = item.thumb ? thumb(item.thumb, item.name) : el("span", "w-thumb");
        tile.append(el("small", "w-thumb-name", item.name));
        return tile;
      }) : [empty("no whiteboards yet")]));
    };
  }
};

// a picture tile that is a real button: focusable, 44pt at least
function thumbButton(url: string, label: string, run: () => void): HTMLElement {
  const tile = thumb(url, label);
  const button = document.createElement("button");
  button.type = "button";
  button.className = "w-thumb w-thumb-button";
  button.setAttribute("aria-label", label);
  button.append(...Array.from(tile.childNodes));
  button.addEventListener("click", run);
  return button;
}

const references: WidgetDef = {
  id: "references", title: "references", view: "references", sizes: ["m", "l"], size: "m", events: [CHANNELS.referencesUpdated],
  build(body) {
    const strip = el("div", "w-strip");
    body.append(strip, pill(t("shuffle"), () => void paint(), false));
    const paint = async () => {
      const refs: ReferenceEntry[] = refsNow().length ? refsNow() : await loadRefs().catch(() => []);
      const stills = refs.filter((ref) => (ref.kind || "image") === "image" && (ref.thumbUrl || ref.imageUrl));
      await loadRefFavorites();
      const shuffled = [...stills].sort(() => Math.random() - 0.5);
      const pick = [...shuffled.filter((ref) => isRefFavorite(ref.id)), ...shuffled.filter((ref) => !isRefFavorite(ref.id))].slice(0, 4);
      strip.replaceChildren(...(pick.length ? pick.map((ref) => {
        return thumbButton(ref.thumbUrl || ref.imageUrl, ref.author || t("reference"), () => void openReference(ref.id));
      }) : [empty("no references yet")]));
    };
    return paint;
  }
};

const ocs: WidgetDef = {
  id: "ocs", title: "characters", view: "ocs", sizes: ["m", "l"], size: "m", events: [CHANNELS.ocsUpdated],
  build(body) {
    const strip = el("div", "w-strip");
    body.append(strip);
    return async () => {
      const sheets = await call<CharacterSheet[]>(CHANNELS.ocsList);
      const mine = sheets.filter((sheet) => sheet.imageUrl);
      strip.replaceChildren(...(mine.length ? mine.slice(0, 6).map((sheet) => {
        const tile = thumbButton(sheet.imageUrl, sheet.name, () => void openOc(sheet.id));
        tile.append(el("small", "w-thumb-name", sheet.name));
        return tile;
      }) : [empty("no characters yet")]));
    };
  }
};

const tierlists: WidgetDef = {
  id: "tierlists", title: "tier lists", view: "tierlists", sizes: ["s", "m", "l"], size: "m", events: [CHANNELS.tierlistsUpdated],
  build(body) {
    const list = el("ul", "w-list");
    body.append(list);
    return async () => {
      const lists = (await call<TierListSummary[]>(CHANNELS.tierlistsList)).slice(0, 4);
      list.replaceChildren(...(lists.length ? lists.map((item) => line(item.title, t("{ranked} of {count} ranked", { ranked: item.ranked, count: item.count }))) : [empty("no tier lists yet")]));
    };
  }
};

const gesture: WidgetDef = {
  id: "gesture", title: "gesture drawing", view: "gesture", sizes: ["s", "m"], size: "s",
  build(body) {
    const row = el("div", "w-keys wrap");
    row.append(pill(t("5 × 30s"), () => startGesture(30, 5)), pill(t("10 × 1m"), () => startGesture(60, 10), true), pill(t("20 × 2m"), () => startGesture(120, 20)));
    body.append(el("p", "w-text", t("a warm up from the pose library, pick a length")), row);
    return () => undefined;
  }
};

const feed: WidgetDef = {
  id: "feed", title: "feed", view: "feed", sizes: ["m", "l"], size: "m", events: [CHANNELS.feedUpdated],
  build(body) {
    const list = el("ul", "w-list");
    body.append(list);
    return async () => {
      const latest: FeedItem[] = (await loadFeed()).slice(0, 4);
      list.replaceChildren(...(latest.length ? latest.map((item) => {
        const row = line(`${item.name} ${feedLine(item)}`, ago(item.at));
        if (item.image) row.prepend(thumb(item.image, item.name));
        return row;
      }) : [empty("nothing on the feed yet")]));
    };
  }
};

const profile: WidgetDef = {
  id: "profile", title: "me", view: "profile", sizes: ["s", "m"], size: "m",
  build(body) {
    const name = el("b", "w-music-title");
    const meta = el("small", "w-line-meta");
    body.append(name, meta);
    return () => {
      const mine = awardsNow();
      name.textContent = myName();
      meta.textContent = mine ? t("{points} points · rank {rank}", { points: mine.points.toLocaleString(locale()), rank: mine.rank || "-" }) : "";
    };
  }
};

// ---- fractal only

const logs: WidgetDef = {
  id: "logs", title: "logs", view: "logs", sizes: ["m", "l"], size: "m", events: [CHANNELS.logsEntry],
  build(body) {
    const list = el("ul", "w-list");
    body.append(list);
    return async () => {
      const entries = (await call<LogEntry[]>(CHANNELS.logsList)).slice(-5).reverse();
      list.replaceChildren(...(entries.length ? entries.map((entry) => line(entry.message, `${entry.level} · ${entry.source}`)) : [empty("nothing logged")]));
    };
  }
};

const claude: WidgetDef = {
  id: "claude", title: "claude usage", view: "claude", sizes: ["s", "m"], size: "s",
  build(body) {
    const spend = el("b", "w-clock");
    const meta = el("small", "w-line-meta");
    const bar = el("span", "w-bar");
    const fill = el("i");
    bar.append(fill);
    body.append(spend, meta, bar);
    return async () => {
      const usage = await call<ClaudeUsage>(CHANNELS.claudeUsage);
      spend.textContent = `$${usage.totalUsd.toFixed(2)}`;
      meta.textContent = usage.budgetUsd ? t("of ${budget} this month", { budget: usage.budgetUsd }) : t("this month");
      fill.style.transform = `scaleX(${usage.budgetUsd ? Math.min(1, usage.totalUsd / usage.budgetUsd) : 0})`;
    };
  }
};

const box: WidgetDef = {
  id: "box", title: "server", view: "server", sizes: ["m", "l"], size: "m",
  build(body) {
    const stats = el("small", "w-line-meta");
    const list = el("ul", "w-list");
    body.append(stats, list);
    return async () => {
      const [overview, bots] = await Promise.all([call<ServerOverview>(CHANNELS.serverOverview), call<BotStatus[]>(CHANNELS.serverBots)]);
      stats.textContent = `${t("up")} ${overview.uptime} · ${t("load")} ${overview.load}`;
      list.replaceChildren(...bots.map((bot) => line(bot.name, bot.health)));
    };
  }
};

const people: WidgetDef = {
  id: "people", title: "people", view: "people", sizes: ["s", "m"], size: "s",
  build(body) {
    const list = el("ul", "w-list");
    body.append(list);
    return async () => {
      const board = await call<PeopleBoard>(CHANNELS.peopleList);
      list.replaceChildren(...(board.people.length ? board.people.slice(0, 5).map((person) => line(person.name, `$${person.spentUsd.toFixed(2)}`)) : [empty("nobody added yet")]));
    };
  }
};

const sharing: WidgetDef = {
  id: "sharing", title: "sharing", view: "sharing", sizes: ["s", "m"], size: "s",
  build(body) {
    const meta = el("p", "w-text");
    body.append(meta);
    return async () => {
      const board = await call<ShareBoard>(CHANNELS.sharesGet);
      const out = Object.values(board.mine).reduce((sum, kinds) => sum + Object.values(kinds).filter(Boolean).length, 0);
      meta.textContent = t("{out} things shared by you, {incoming} people share with you", { out, incoming: board.withMe.length });
    };
  }
};

export const WIDGETS: WidgetDef[] = [feed, music, calendar, timer, palette, prompts, jobs, calc, roulette, maps, scan, doodles, whiteboard, references, ocs, tierlists, gesture, profile, logs, claude, box, people, sharing];

export function widgetFeature(def: WidgetDef): string {
  if (OWNER_PLACES.includes(def.id)) return "owner";
  return FEATURES.find((feature) => feature.places.includes(def.id))?.id || "";
}

const refreshers = new Map<string, () => void | Promise<void>>();

export function refreshWidget(id: string): void {
  const mod = document.querySelector<HTMLElement>(`#home-rack > .mod[data-mod="place-${id}"]`);
  if (!mod || mod.classList.contains("mod-off") || mod.classList.contains("feature-off")) return;
  void Promise.resolve(refreshers.get(id)?.()).catch(() => undefined);
}

export function refreshAllWidgets(): void {
  WIDGETS.forEach((def) => refreshWidget(def.id));
}

// makes each widget's markup. it stays hidden until it is added from the shelf, so home keeps its own shape
export function buildWidgets(rack: HTMLElement, register: (id: string, def: WidgetDef) => void): void {
  for (const def of WIDGETS) {
    const id = `place-${def.id}`;
    register(id, def);
    const mod = el("section", "panel mod widget");
    mod.dataset.mod = id;
    const feature = widgetFeature(def);
    if (feature) {
      mod.dataset.feature = feature;
      mod.classList.toggle("feature-off", feature === "owner" ? !amOwner() : !canUse(feature));
    }
    const head = el("div", "section-head");
    head.append(el("h2", "", t(def.title)));
    head.append(pill(t("open"), () => showView(def.view, def.detail || "")));
    const body = el("div", "w-body stack");
    mod.append(head, body);
    refreshers.set(def.id, def.build(body));
    for (const channel of def.events || []) listen(channel, () => refreshWidget(def.id));
    rack.append(mod);
  }
  stampIcons(rack);
  onViewShown("today", refreshAllWidgets);
  // awards change what the profile widget says
  onAwards(() => refreshWidget("profile"));
}
